import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import testsService, { AllTestsResult } from '../../services/testsService';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';

interface TestFile {
  id: string; name: string; path: string; description: string;
  category: string; type: 'python' | 'typescript' | 'integration' | 'e2e';
}

interface TestResult { success: boolean; output: string; duration: number; timestamp: string; }

const TEST_FILES: TestFile[] = [
  { id: 'test-auth-api', name: 'Authentication API Tests', path: '/tests/backend/test_auth_api.py', description: 'Tests for authentication API endpoints', category: 'backend', type: 'python' },
  { id: 'test-auth', name: 'Authentication Core Tests', path: '/tests/backend/test_auth.py', description: 'Core authentication functionality tests', category: 'backend', type: 'python' },
  { id: 'test-auth-final', name: 'Authentication Final Tests', path: '/tests/backend/test_auth_final.py', description: 'Comprehensive authentication test suite', category: 'backend', type: 'python' },
  { id: 'test-authority-direct', name: 'Authority Direct Tests', path: '/tests/backend/test_authority_direct.py', description: 'Direct authority management tests', category: 'backend', type: 'python' },
  { id: 'test-project-inventory', name: 'Project Inventory Lifecycle', path: '/tests/backend/test_project_inventory_lifecycle.py', description: 'Project inventory lifecycle tests', category: 'backend', type: 'python' },
  { id: 'test-risk-endpoints', name: 'Risk Management Endpoints', path: '/tests/backend/test_risk_endpoints.py', description: 'Risk management API endpoint tests', category: 'backend', type: 'python' },
  { id: 'test-login-integration', name: 'Login Integration Tests', path: '/tests/integration/test_login_integration.py', description: 'End-to-end login integration tests', category: 'integration', type: 'integration' },
  { id: 'e2e-login', name: 'E2E Login Tests', path: '/playwright-e2e-tests/src/tests/auth/login.spec.ts', description: 'Playwright end-to-end login tests', category: 'e2e', type: 'e2e' },
  { id: 'e2e-logout', name: 'E2E Logout Tests', path: '/playwright-e2e-tests/src/tests/auth/logout.spec.ts', description: 'Playwright end-to-end logout tests', category: 'e2e', type: 'e2e' },
  { id: 'e2e-login-visual', name: 'E2E Visual Login Tests', path: '/playwright-e2e-tests/src/tests/login-visual.spec.ts', description: 'Playwright visual regression tests for login', category: 'e2e', type: 'e2e' },
  { id: 'create-test-users', name: 'Create Test Users', path: '/tests/utils/create_test_users.py', description: 'Utility to create test user data', category: 'utils', type: 'python' },
];

const CATEGORY_META = [
  { id: 'backend',     name: 'Backend Tests',     icon: '🐍' },
  { id: 'integration', name: 'Integration Tests',  icon: '🔗' },
  { id: 'e2e',         name: 'E2E Tests',          icon: '🎭' },
  { id: 'utils',       name: 'Test Utilities',     icon: '🛠️' },
];

type SectionKey = 'backend' | 'integration' | 'e2e' | 'utils';

const TestsTab: React.FC = () => {
  const [selectedFile, setSelectedFile]     = useState<TestFile | null>(null);
  const [testOutput, setTestOutput]         = useState<string>('');
  const [running, setRunning]               = useState(false);
  const [error, setError]                   = useState<string | null>(null);
  const [searchTerm, setSearchTerm]         = useState('');
  const [activeSection, setActiveSection]   = useState<SectionKey>('backend');
  const [testResult, setTestResult]         = useState<TestResult | null>(null);
  const [allTestsRunning, setAllTestsRunning] = useState(false);
  const [allTestsResult, setAllTestsResult] = useState<AllTestsResult | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [testRunnerHealthy, setTestRunnerHealthy] = useState(true);
  const [restartingRunner, setRestartingRunner] = useState(false);
  const [page, setPage]                     = useState(1);
  const [pageSize, setPageSize]             = useState(10);

  const activeCategory = CATEGORY_META.find(c => c.id === activeSection)!;

  useEffect(() => { checkRunnerHealth(); }, []);
  useEffect(() => { setPage(1); setSelectedFile(null); }, [activeSection, searchTerm, pageSize]);

  const checkRunnerHealth = async () => {
    try { setTestRunnerHealthy(await testsService.getHealthStatus()); }
    catch { setTestRunnerHealthy(false); }
  };

  const handleFileSelect = async (file: TestFile) => {
    setSelectedFile(file); setRunning(true); setError(null);
    setTestOutput('Starting test execution…\n'); setTestResult(null);
    try {
      const result = await testsService.runTest(file.path, file.type);
      setTestOutput(result.output || 'No output available');
      setTestResult({ success: result.success, output: result.output, duration: result.duration, timestamp: new Date().toISOString() });
      if (result.error) setError(result.error);
    } catch (err: any) {
      const msg = err.message || 'Failed to run test';
      setError(msg); setTestOutput(`Error: ${msg}\n`);
      if (msg.includes('Cannot reach external test runner')) setTestRunnerHealthy(false);
    } finally { setRunning(false); }
  };

  const handleRunAllTests = async () => {
    setAllTestsRunning(true); setError(null);
    setTestOutput('🚀 Starting comprehensive test suite…\n\n'); setAllTestsResult(null); setShowStatistics(false);
    try {
      const result = await testsService.runAllTests();
      setAllTestsResult(result); setShowStatistics(true);
      let output = `📊 Test Suite Complete!\n═══════════════════\n\n📈 SUMMARY:\n${result.summary}\n\n`;
      output += `📋 STATISTICS:\n• Total: ${result.statistics.total_tests}\n• ✅ Passed: ${result.statistics.passed}\n• ❌ Failed: ${result.statistics.failed}\n• 📈 Success Rate: ${result.statistics.success_rate}%\n• ⏱️ Duration: ${result.statistics.total_duration}s\n`;
      setTestOutput(output);
    } catch (err: any) {
      const msg = err.message || 'Failed to run all tests';
      setError(msg); setTestOutput(`❌ Error: ${msg}\n`);
      if (msg.includes('Cannot reach external test runner')) setTestRunnerHealthy(false);
    } finally { setAllTestsRunning(false); }
  };

  const handleRestartTestRunner = async () => {
    setRestartingRunner(true); setError(null);
    try {
      const result = await testsService.restartTestRunner();
      if (result.success) { setTestRunnerHealthy(true); setTestOutput(`✅ ${result.message}\n`); }
      else { setError(result.message); setTestOutput(`❌ ${result.message}\n`); }
    } catch (err: any) {
      const msg = err.message || 'Failed to restart test runner';
      setError(msg); setTestOutput(`❌ Error: ${msg}\n`);
    } finally { setRestartingRunner(false); setTimeout(checkRunnerHealth, 1000); }
  };

  const filteredFiles = TEST_FILES.filter(f => {
    const matchesSection = f.category === activeSection;
    const matchesSearch  = !searchTerm.trim() ||
      f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.path.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSection && matchesSearch;
  });

  const totalRecords   = filteredFiles.length;
  const paginatedFiles = filteredFiles.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="flex h-full overflow-hidden">

      {/* ── Left nav card ──────────────────────────────────────── */}
      <div className="netbody-card side-nav-card">
        <div className="side-nav-card-heading">Test Category</div>
        <nav aria-label="Test category navigation">
          {CATEGORY_META.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveSection(cat.id as SectionKey)}
              className={`side-panel-menu-item${activeSection === cat.id ? ' side-panel-menu-item--active' : ''}`}
              data-testid={`NAV_tests_category_${cat.id}`}
            >
              <span style={{ marginRight: '6px' }}>{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Right card: toolbar + two-panel content ────────────── */}
      <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">

        {/* Toolbar */}
        <div className="netbody-header">
          <div className="flex items-center gap-2 ml-auto">
            {/* Runner health badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '4px', background: testRunnerHealthy ? '#f0fdf4' : '#fef2f2', border: `1px solid ${testRunnerHealthy ? '#bbf7d0' : '#fecaca'}` }}>
              <span style={{ fontSize: '11px', color: testRunnerHealthy ? '#16a34a' : '#dc2626' }}>
                {testRunnerHealthy ? '✅ Online' : '❌ Offline'}
              </span>
              <Button variant="secondary-outline" onClick={checkRunnerHealth} title="Refresh health" style={{ padding: '1px 4px', fontSize: '11px' }} data-testid="BTN_tests_health_refresh">🔄</Button>
              {!testRunnerHealthy && (
                <Button variant="fill" onClick={handleRestartTestRunner} disabled={restartingRunner} style={{ fontSize: '11px', background: '#dc2626', border: 'none', padding: '1px 6px' }} data-testid="BTN_tests_runner_start">
                  {restartingRunner ? '…' : 'Start'}
                </Button>
              )}
            </div>
            <FormControl.Input
              type="text"
              placeholder={`Search ${activeCategory.name.toLowerCase()}…`}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_tests_search"
            />
            <Button variant="fill" onClick={handleRunAllTests} disabled={allTestsRunning || running} data-testid="BTN_tests_run_all">
              {allTestsRunning ? '⟳ Running…' : '🚀 Run All Tests'}
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden" style={{ display: 'flex' }}>

            {/* Test file table */}
            <div className="table-list" style={{ width: '380px', flexShrink: 0, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div className="table-list-scroll" style={{ flex: 1, overflowY: 'auto' }}>
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Test Name</th>
                      <th className="table-list-th table-list-th--right">Run</th>
                    </tr>
                  </thead>
                  <tbody aria-live="polite">
                    {filteredFiles.length === 0 && (
                      <tr>
                        <td colSpan={3} className="table-list-empty-cell">
                          No {activeCategory.name.toLowerCase()} found.
                        </td>
                      </tr>
                    )}
                    {paginatedFiles.map((f, idx) => (
                      <tr
                        key={f.id}
                        className={`table-list-row${selectedFile?.id === f.id ? ' table-list-row--selected' : ''}`}
                        onClick={() => handleFileSelect(f)}
                        style={{ cursor: 'pointer' }}
                        data-testid={`BTN_tests_row_${f.id}_select`}
                      >
                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                        <td className="table-list-cell">
                          <div className="table-list-cell-title">{f.name}</div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{f.description}</div>
                        </td>
                        <td className="table-list-cell table-list-cell--actions">
                          <button
                            className="table-list-action table-list-action--edit"
                            title="Run test"
                            disabled={running}
                            onClick={e => { e.stopPropagation(); handleFileSelect(f); }}
                            data-testid={`BTN_tests_row_${f.id}_run`}
                          >
                            ▶
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                recordLabel="tests"
              />
            </div>

            {/* Terminal panel */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {selectedFile ? (
                <>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#fff', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 600, color: '#111827' }}>{selectedFile.name}</h3>
                        <p style={{ margin: '0 0 2px', color: '#6b7280', fontSize: '12px' }}>{selectedFile.description}</p>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{selectedFile.path}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button variant="secondary-outline" onClick={() => { setTestOutput(''); setTestResult(null); setError(null); }} disabled={running} data-testid="BTN_tests_clear">Clear</Button>
                        <Button variant="fill" onClick={() => handleFileSelect(selectedFile)} disabled={running} data-testid="BTN_tests_run_selected">
                          {running ? '⟳ Running…' : '▶ Run Test'}
                        </Button>
                      </div>
                    </div>
                    {testResult && (
                      <div style={{ padding: '6px 10px', borderRadius: '4px', background: testResult.success ? '#f0fdf4' : '#fef2f2', border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`, fontSize: '12px', color: testResult.success ? '#16a34a' : '#dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {testResult.success ? '✅ Test Passed' : '❌ Test Failed'}
                        <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#6b7280' }}>{testResult.duration}ms</span>
                      </div>
                    )}
                    {showStatistics && allTestsResult && (
                      <div style={{ marginTop: '8px', padding: '10px', borderRadius: '6px', background: allTestsResult.success ? '#f0fdf4' : '#fff7ed', border: `1px solid ${allTestsResult.success ? '#bbf7d0' : '#fed7aa'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <strong style={{ fontSize: '13px', color: allTestsResult.success ? '#16a34a' : '#ea580c' }}>{allTestsResult.success ? '🎉' : '⚠️'} Test Suite Results</strong>
                          <Button variant="secondary-outline" onClick={() => setShowStatistics(false)} style={{ padding: '0 6px', color: '#9ca3af', border: 'none', background: 'none' }} data-testid="BTN_tests_stats_close">×</Button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', textAlign: 'center' }}>
                          {([['Files', allTestsResult.statistics.total_files], ['Tests', allTestsResult.statistics.total_tests], ['Passed', allTestsResult.statistics.passed], ['Failed', allTestsResult.statistics.failed], ['Errors', allTestsResult.statistics.errors], ['Success %', `${allTestsResult.statistics.success_rate}%`]] as [string, string | number][]).map(([label, val]) => (
                            <div key={label}>
                              <div style={{ fontSize: '16px', fontWeight: 700, color: '#374151' }}>{val}</div>
                              <div style={{ fontSize: '10px', color: '#9ca3af' }}>{label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1, padding: '12px 16px', overflowY: 'auto', background: '#1e1e1e', color: '#d4d4d4', fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.4' }}>
                    {error
                      ? <div style={{ color: '#ff6b6b' }}>Error: {error}</div>
                      : <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{testOutput || 'Click "Run Test" to execute…'}</pre>
                    }
                  </div>
                </>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#6b7280' }}>
                  <div>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>🧪</div>
                    <h3 style={{ margin: '0 0 8px', color: '#374151', fontSize: '16px' }}>Select a test to run</h3>
                    <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>Choose a test file from the list to execute it and view results.</p>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

    </div>
  );
};

export default TestsTab;
